import { useDispatch } from 'react-redux';
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { BaseCard } from '@/components/uiKit/Cards';
import { FUSION_TRENDS_TYPE } from '@/const';
import { Chart } from '@/modules/shared/Charts';
import { BaseTooltip } from '@/components';
import { Skeleton } from '@/components/ui/skeleton';
import { useAppFilters } from '@/hooks/shared';
import { setSelectedDiagnostic } from '@/store/slices/assetStudioSlice';
import { ASSET_STUDIO } from '@/types/shared/navigation-tabs.enums';
import { Diagnostics } from '@/types/api/diagnostics.types';
import CardTitle from '@/components/uiKit/Cards/CardTitle';
import { useFilteredDiagnostics } from '@/hooks';
import { ChartLineIcon, CircleInfoIcon, NoResults } from '@/assets/Icons';
import { Icon } from '@/components/shared/display/Icon';

interface FusionTrendProps {
  assetName: string;
  diagnostics: Diagnostics[];
  isLoading: boolean;
  showMaintenance?: boolean;
  baseTitle?: string;
}

export const FusionTrendCard = ({
  assetName,
  diagnostics,
  isLoading,
  showMaintenance,
  baseTitle,
}: FusionTrendProps) => {
  const dispatch = useDispatch();
  const { selectedComponent } = useAppFilters();
  const { filteredDiagnostics, activeChartPoint, setActiveChartPoint } = useFilteredDiagnostics(diagnostics);
  const onPointClick = useCallback((value: [string, number]) => {
    setActiveChartPoint(value);
  }, []);
  useEffect(() => {
    if (activeChartPoint?.[0]) {
      const selectedDiagnostic = filteredDiagnostics?.find((el: any) => el[0] === activeChartPoint[0]);
      dispatch(setSelectedDiagnostic(selectedDiagnostic));
    }
  }, [activeChartPoint, dispatch, selectedComponent]);

  return isLoading ? (
    <Skeleton className="w-full h-full max-h-[200px]" />
  ) : (
    <BaseCard
      variant="wrapper"
      classNames={{
        card: 'min-h-0 flex flex-col w-full min-h-[180px]',
        content: 'min-h-0 flex flex-col flex-1',
        title: 'h-[30px] flex items-center min-w-full justify-between',
      }}
      title={
        <CardTitle
          icon={<Icon icon={ChartLineIcon} />}
          text={
            <div className="flex justify-between items-center gap-2">
              <div className="w-full flex items-center">
                {`${baseTitle || assetName}  Trend`}{' '}
                <BaseTooltip
                  className="pb-2"
                  content="The health status is determined by the most severe diagnostic of each day"
                >
                  <span>
                    <Icon icon={CircleInfoIcon} className="w-5 h-5" />
                  </span>
                </BaseTooltip>
              </div>
            </div>
          }
        />
      }
    >
      {filteredDiagnostics?.length > 0 ? (
        <Chart
          type={
            selectedComponent !== ASSET_STUDIO.ALL_COMPONENTS
              ? FUSION_TRENDS_TYPE.Component
              : FUSION_TRENDS_TYPE.Machine
          }
          diagnostics={filteredDiagnostics}
          selectedComponent={{ component_id: selectedComponent }}
          onPointClick={onPointClick}
          activeChartPoint={activeChartPoint}
          showMaintenance={showMaintenance}
        />
      ) : (
        <div className="flex items-center justify-center min-w-full min-h-full flex-col gap-4">
          <Icon icon={NoResults} className="w-16 h-16" />

          <div className="text-center flex-wrap">
            No results for the selected date range. <br /> Try adjusting the date range.
          </div>
        </div>
      )}
    </BaseCard>
  );
};
